import {useToast, type ToastProps} from '@linx-security/ui/Toast';
import {Text} from '@linx-security/ui';
import {useCallback, useEffect} from 'react';

import {failedTasksSelector, pendingTasksSelector, successfulTasksSelector} from './selectors';
import type {BackgroundTasksStore, Task} from './types';
import {useBackgroundTasksStore} from './useBackgroundTasks';

/**
 * A hook that subscribes to background tasks store and shows UI elements to notify the user about the tasks status.
 */
export const useBackgroundTasksNotifications = () => {
  const toast = useToast({
    position: `bottom-right`,
    variant: `primary`,
  });

  const updateTask = useBackgroundTasksStore(state => state.updateTask);
  const failedTasks = useBackgroundTasksStore(failedTasksSelector, areEqual);
  const pendingTasks = useBackgroundTasksStore(pendingTasksSelector, areEqual);
  const successTasks = useBackgroundTasksStore(successfulTasksSelector, areEqual);

  // Helper function to handle task notifications
  const handleTaskNotifications = useCallback(
    (
      tasks: Map<string, Task>,
      status: ToastProps['status'],
      toastIdPrefix: string,
      messageExtractor: (msg: any, taskId: string) => string,
      extraCondition: (task: Task) => boolean = () => true,
    ) => {
      tasks.forEach((task, taskId) => {
        const {type, isNotified, isFetching, toast: {id, ...msg} = {}} = task;
        // Use provided toast id or generate one based on the task id
        const toastId = `${toastIdPrefix}-${id ?? taskId}`;

        // Show notification if the task is not yet notified and meets extra conditions
        if (!isNotified && !task.silent && extraCondition(task) && !toast.isActive(toastId)) {
          toast({
            title: <Text whiteSpace={`nowrap`}>{messageExtractor(msg, taskId)}</Text>,
            status,
            id: toastId,
            duration: status === `loading` ? 10_000 * 1_000 : undefined,
            isClosable: true,
            retry: typeof task.toast?.retry === `function` ? task.toast.retry(status) : task.toast?.retry,
          });
          updateTask(taskId, {type, isNotified: true});
        } else if (status !== `loading` && !isFetching && toast.isActive(`task-pending-${taskId}`)) {
          // Close the pending task notification if it is active
          toast.close(`task-pending-${taskId}`);
        }
      });
    },
    [toast, updateTask],
  );

  // Effect to handle failed tasks notifications
  useEffect(() => {
    handleTaskNotifications(
      failedTasks,
      `error`,
      `task-failed`,
      (msg, taskId) => msg?.error || `Task ${taskId} failed`,
    );
  }, [updateTask, failedTasks, toast, handleTaskNotifications]);

  // Effect to handle pending tasks notifications
  useEffect(() => {
    handleTaskNotifications(
      pendingTasks,
      `loading`,
      `task-pending`,
      (msg, taskId) => msg?.loading || `Task ${taskId} is being processed`,
      task => !!task.isFetching,
    );
  }, [updateTask, pendingTasks, toast, handleTaskNotifications]);

  // Effect to handle successful tasks notifications
  useEffect(() => {
    handleTaskNotifications(
      successTasks,
      `success`,
      `task-success`,
      (msg, taskId) => msg?.success || `Task ${taskId} completed successfully`,
    );
  }, [updateTask, successTasks, toast, handleTaskNotifications]);
};

const areEqual = (state: BackgroundTasksStore['tasks'], nextState: BackgroundTasksStore['tasks']): boolean => {
  if (state.size !== nextState.size) {
    return false;
  }

  for (const [taskId, task] of state) {
    const nextTask = nextState.get(taskId);

    if (!nextTask || nextTask.isNotified !== task.isNotified) {
      return false;
    }
  }

  return true;
};
